import React, { FC, useState } from 'react';

import { alpha, styled } from '@mui/system';
import { Stack, Theme, Typography, useTheme } from '@mui/material';

import DoneIcon from '@mui/icons-material/Done';

import { IconFilePlus, IconSitemap, TablerIcon } from '@tabler/icons';

interface ChooseBoxProps {
    theme: Theme;
    isSelected?: boolean;
}

const ChooseBox = styled('div')(({ theme, isSelected }: ChooseBoxProps) => ({
    position: 'relative',
    padding: 16,
    minHeight: 220,
    minWidth: 260,
    borderRadius: 16,
    border: '1px solid',
    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.secondary.light,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.5s ease-out ',
    ...(isSelected && {
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.secondary.main,
        background: theme.palette.mode === 'dark' ? alpha(theme.palette.dark.light, 0.1) : alpha(theme.palette.secondary.main, 0.2),
        '& .success-icon': {
            position: 'absolute',
            top: '-8px',
            fontSize: '1rem'
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-12.5px',
            width: '25px',
            height: '25px',
            borderRadius: '50%',
            display: 'inline-block',
            fontSize: '10px',
            background: theme.palette.mode === 'dark' ? theme.palette.dark.dark : theme.palette.common.white,
            border: '2px solid '
        }
    })
}));

interface Props {
    descriptionTitle?: string;
    createType: number;
    firstTypeOption: string;
    firstTypeIcon?: TablerIcon;
    secondTypeOption: string;
    secondTypeIcon?: TablerIcon;
    onChange: (type: number) => void;
}
const ChooseType: FC<Props> = (props) => {
    const { descriptionTitle, createType, firstTypeOption, secondTypeOption, firstTypeIcon, secondTypeIcon, onChange } = props;
    const theme = useTheme();

    const handleChangeCreateType = (event: React.MouseEvent<HTMLDivElement>, type: number) => {
        event.preventDefault();
        onChange(type);
    };

    const FirstTypeIcon = firstTypeIcon!;
    const firstIcon = firstTypeIcon ? <FirstTypeIcon stroke={1} size="5rem" /> : <IconFilePlus size="5rem" stroke={1} />;

    const SecondTypeIcon = secondTypeIcon!;
    const secondIcon = secondTypeIcon ? <SecondTypeIcon stroke={1} size="5rem" /> : <IconSitemap size="5rem" stroke={1} />;

    return (
        <Stack direction="column" spacing={3}>
            {descriptionTitle && (
                <Typography variant="body1" component="span">
                    {descriptionTitle}
                </Typography>
            )}
            <Stack direction="row" spacing={1} mt={descriptionTitle ? 0 : 3} justifyContent="center">
                <ChooseBox theme={theme} isSelected={createType === 0} onClick={(event) => handleChangeCreateType(event, 0)}>
                    {createType === 0 && <DoneIcon className="success-icon" />}
                    <Stack direction="column" alignItems="center" spacing={1}>
                        {firstIcon}
                        <Typography variant="h5" component="span">
                            {firstTypeOption}
                        </Typography>
                    </Stack>
                </ChooseBox>

                <ChooseBox theme={theme} isSelected={createType === 1} onClick={(event) => handleChangeCreateType(event, 1)}>
                    {createType === 1 && <DoneIcon className="success-icon" />}
                    <Stack direction="column" alignItems="center" spacing={1}>
                        {secondIcon}
                        <Typography variant="h5" component="span">
                            {secondTypeOption}
                        </Typography>
                    </Stack>
                </ChooseBox>
            </Stack>
        </Stack>
    );
};

export default ChooseType;
